import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MultiSelect from '../../Components/utils/MultiSelect';
import Dropdown from '../../Components/utils/Dropdown';
import { FaCheck, FaCamera, FaPen, FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import image1 from '../../images/pngs/signUp4.png';
import image2 from '../../images/pngs/signUp1.png';
import image3 from '../../images/pngs/signUp3.png';
import image4 from '../../images/pngs/signUp4.png';
import firstBackground from '../../images/svgs/signUp3.svg';
import secondBackground from '../../images/svgs/signUp5.svg';
import axios from 'axios';
import { showToast } from '../../Components/utils/ShowToast';
import { LoginContext } from '../../Contexts/LoginContext';
import Loader from '../../Components/utils/Loader';
import countries from '../../data/countries.json';
import { states } from '../../data/constants';

const API = process.env.REACT_APP_Backend_Host_Name;

const EMPLOYEE_OPTIONS = [
    { value: '1-20', label: '1-20' },
    { value: '21-50', label: '21-50' },
    { value: '51-100', label: '51-100' },
    { value: '101-250', label: '101-250' },
    { value: '251-500', label: '251-500' },
    { value: '501-999', label: '501-999' },
    { value: '1000+', label: '1000+' },
];

const ORG_OPTIONS = [
    { value: 'Manufacturer', label: 'Manufacturer' },
    { value: 'Exporter', label: 'Exporter' },
    { value: 'Wholesaler', label: 'Wholesaler' },
    { value: 'Retailer', label: 'Retailer' },
    { value: 'Other', label: 'Other' },
];

const SELLER_TYPE_OPTIONS = [
    { value: 'individual', label: 'Individual' },
    { value: 'company', label: 'Company' },
];

const StepTab = ({ label, isActive, isCompleted, isClickable, onClick, text }) => (
    <div
        className={`step-tab ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isClickable ? 'clickable' : 'disabled'}`}
        onClick={isClickable ? onClick : undefined}
    >
        <div className="step-number">
            {isCompleted ? <FaCheck className="check-icon" /> : <span>{label}</span>}
        </div>
        <div className="step-label">{text}</div>
    </div>
);

const TagInput = ({ label, values, onChange, placeholder, error, required = false, numericOnly = false }) => {
    const [input, setInput] = useState('');
    const [inputError, setInputError] = useState('');

    const addTag = () => {
        const next = input.trim();
        if (!next || values.includes(next)) return;

        if (numericOnly && !/^\d+$/.test(next)) {
            setInputError('Only numbers are allowed for this field');
            return;
        }

        onChange([...values, next]);
        setInputError('');
        setInput('');
    };

    const removeTag = (tag) => {
        onChange(values.filter((v) => v !== tag));
    };

    return (
        <div>
            <label className="label" style={{ display: 'block', marginBottom: '10px' }}>
                {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
            </label>
            <div className="becomeSellerTagInput">
                {values.length > 0 && (
                    <div className="becomeSellerTagList">
                        {values.map((tag) => (
                            <span key={tag} className="becomeSellerTagChip">
                                {tag}
                                <button type="button" onClick={() => removeTag(tag)}>x</button>
                            </span>
                        ))}
                    </div>
                )}
                <div className="becomeSellerTagRow">
                    <input
                        type="text"
                        className="inputField"
                        value={input}
                        onChange={(e) => {
                            const nextValue = numericOnly
                                ? e.target.value.replace(/[^\d]/g, '')
                                : e.target.value;
                            setInput(nextValue);
                            if (inputError) setInputError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addTag();
                            }
                        }}
                        placeholder={placeholder}
                    />
                    <button type="button" className="primaryBtn" style={{ width: "fit-content" }} onClick={addTag}>Add</button>
                </div>
            </div>
            {inputError && <div className="error-message">{inputError}</div>}
            {error && <div className="error-message">{error}</div>}
        </div>
    );
};

const normalizeEmployeeRange = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && EMPLOYEE_OPTIONS.some((opt) => opt.value === value)) {
        return value;
    }

    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    if (num <= 20) return '1-20';
    if (num <= 50) return '21-50';
    if (num <= 100) return '51-100';
    if (num <= 250) return '101-250';
    if (num <= 500) return '251-500';
    if (num <= 999) return '501-999';
    return '1000+';
};

const employeeRangeToApprox = (range) => {
    switch (range) {
        case '1-20': return 20;
        case '21-50': return 50;
        case '51-100': return 100;
        case '101-250': return 250;
        case '251-500': return 500;
        case '501-999': return 999;
        case '1000+': return 1000;
        default: return 0;
    }
};

const buildB2BPayload = (formData) => ({
    companyName: formData.companyName.trim(),
    telephone: formData.telephone,
    fax: formData.fax,
    natureOfOrg: formData.natureOfOrg,
    natureOfOrgOther: formData.natureOfOrg === 'Other' ? formData.natureOfOrgOther : '',
    numberOfEmployees: formData.numberOfEmployees,
    website: formData.website,
    productCategories: formData.productCategories,
    productionCapacity: formData.productionCapacity,
    inventoryCapacity: formData.inventoryCapacity,
    certifications: formData.certifications.map((name) => ({ name, attachments: [] })),
    exportMarkets: formData.exportMarketsWorldwide ? ['Worldwide'] : formData.exportMarkets,
    yearFounded: formData.yearFounded ? Number(formData.yearFounded) : undefined,
});

const SellerSignUpForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLogin, fetchUserData, user } = useContext(LoginContext);

    const mode = searchParams.get('mode');
    const isB2BUpgradeMode = mode === 'b2b-upgrade';
    const isExistingSeller = user?.role === 'seller' && user?.sellerId?._id;
    const isForcedCompanyMode = Boolean(isExistingSeller && isB2BUpgradeMode);

    const [currentStepNumber, setCurrentStepNumber] = useState(1);
    const [image, setImage] = useState(image1);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isPrefilling, setIsPrefilling] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const [companyModalVisible, setCompanyModalVisible] = useState(false);
    const [companyModalAcknowledged, setCompanyModalAcknowledged] = useState(false);
    const [b2bReviewRequired, setB2bReviewRequired] = useState(true);
    const [b2bReviewConfigLoaded, setB2bReviewConfigLoaded] = useState(false);
    const [showReviewConfirmationModal, setShowReviewConfirmationModal] = useState(false);

    const [b2bProfile, setB2bProfile] = useState(null);
    const [b2bCategories, setB2bCategories] = useState([]);

    const [formData, setFormData] = useState({
        sellerType: isForcedCompanyMode ? SELLER_TYPE_OPTIONS[1] : SELLER_TYPE_OPTIONS[0],
        sellerPicture: null,
        existingSellerPicture: '',

        fullName: '',
        displayName: '',
        services: [],

        country: '',
        state: '',
        city: '',
        address: '',
        zipCode: '',

        briefDescription: '',

        yearEstablished: '',
        noOfEmployees: '',

        companyName: '',
        natureOfOrg: 'Manufacturer',
        natureOfOrgOther: '',
        numberOfEmployees: '1-20',
        yearFounded: '',
        website: '',
        productionCapacity: '',
        inventoryCapacity: '',
        postalCode: '',
        telephone: [],
        fax: [],
        exportMarkets: [],
        exportMarketsWorldwide: false,
        productCategories: [],
        certifications: [],
    });

    const isCompanySeller = formData.sellerType?.value === 'company';

    const getSteps = useMemo(() => {
        const steps = [
            { number: 1, text: 'Seller Type' },
            { number: 2, text: 'Basic Info' },
        ];

        if (isCompanySeller) {
            steps.push(
                { number: 3, text: 'Company Identity' },
                { number: 4, text: 'Company Operations' }
            );
        }

        steps.push(
            { number: steps.length + 1, text: 'Additional Info' },
            { number: steps.length + 2, text: 'Description' }
        );

        return steps;
    }, [isCompanySeller]);

    useEffect(() => {
        if (isLogin === false) {
            showToast('Please login to access that resource', 'info');
            navigate('/signin', { replace: true });
        }
    }, [isLogin, navigate]);

    useEffect(() => {
        if (!isLogin || !user || isInitialized) return;

        const prefillData = async () => {
            setIsPrefilling(true);
            const token = localStorage.getItem('token');

            try {
                const categoryPromise = axios.get(`${API}/api/v1/categories?b2b=true`);

                if (isExistingSeller) {
                    const [sellerRes, b2bRes, categoryRes] = await Promise.all([
                        axios.get(`${API}/api/v1/sellers/seller/${user.sellerId._id}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }),
                        axios.get(`${API}/api/v1/b2b/seller/profile`, {
                            headers: { Authorization: `Bearer ${token}` },
                        }),
                        categoryPromise,
                    ]);

                    const seller = sellerRes.data?.seller;
                    const profile = b2bRes.data?.profile || null;

                    setB2bProfile(profile);
                    setB2bCategories(categoryRes.data?.categories || []);

                    const normalizedSellerType = seller?.sellerType === 'Company'
                        ? SELLER_TYPE_OPTIONS[1]
                        : SELLER_TYPE_OPTIONS[0];

                    const resolvedSellerType = isForcedCompanyMode ? SELLER_TYPE_OPTIONS[1] : normalizedSellerType;

                    const ba = user?.buyerAddress;
                    const sa = seller?.sellerAddress;

                    setFormData((prev) => ({
                        ...prev,
                        sellerType: resolvedSellerType,
                        existingSellerPicture: seller?.profileImage || '',

                        fullName: seller?.fullName || '',
                        displayName: seller?.displayName || '',
                        services: seller?.services || [],

                        country: ba?.country?.name
                            ? {
                                value: ba.country.code || '',
                                label: ba.country.name,
                            }
                            : (sa?.country?.name
                                ? {
                                    value: sa.country.code || '',
                                    label: sa.country.name,
                                }
                                : prev.country),
                        state: ba?.state?.name
                            ? {
                                name: ba.state.name,
                                tax: ba.state.tax || 5,
                            }
                            : (sa?.state?.name
                                ? {
                                    name: sa.state.name,
                                    tax: sa.state.tax || 5,
                                }
                                : prev.state),
                        city: ba?.city || sa?.city || '',
                        address: ba?.address || sa?.address || '',
                        zipCode: ba?.zipCode || '',

                        briefDescription: seller?.description || '',

                        yearEstablished: seller?.yearEstablished || '',
                        noOfEmployees: seller?.noOfEmployees || '',

                        companyName: profile?.companyName || seller?.displayName || '',
                        natureOfOrg: profile?.natureOfOrg || 'Manufacturer',
                        natureOfOrgOther: profile?.natureOfOrgOther || '',
                        numberOfEmployees: profile?.numberOfEmployees || normalizeEmployeeRange(seller?.noOfEmployees) || '1-20',
                        yearFounded: profile?.yearFounded || seller?.yearEstablished || '',
                        website: profile?.website || '',
                        productionCapacity: profile?.productionCapacity || '',
                        inventoryCapacity: profile?.inventoryCapacity || '',
                        postalCode: ba?.zipCode || '',
                        telephone: profile?.telephone || [],
                        fax: profile?.fax || [],
                        exportMarkets: profile?.exportMarkets?.includes('Worldwide') ? [] : (profile?.exportMarkets || []),
                        exportMarketsWorldwide: profile?.exportMarkets?.includes('Worldwide') ?? false,
                        productCategories: profile?.productCategories || [],
                        certifications: (profile?.certifications || []).map((c) => c?.name).filter(Boolean),
                    }));

                    if (isForcedCompanyMode && !companyModalAcknowledged) {
                        setCompanyModalVisible(true);
                    }

                    if (isB2BUpgradeMode && profile?.status === 'approved') {
                        showToast('Your B2B business profile is already approved.', 'success');
                        navigate('/sellerDashboard', { replace: true });
                    }
                } else {
                    const categoryRes = await categoryPromise;
                    setB2bCategories(categoryRes.data?.categories || []);

                    setFormData((prev) => ({
                        ...prev,
                        country: user?.buyerAddress?.country?.name
                            ? {
                                value: user.buyerAddress.country.code || '',
                                label: user.buyerAddress.country.name,
                            }
                            : prev.country,
                        state: user?.buyerAddress?.state?.name
                            ? {
                                name: user.buyerAddress.state.name,
                                tax: user.buyerAddress.state.tax || 5,
                            }
                            : prev.state,
                        city: user?.buyerAddress?.city || '',
                        address: user?.buyerAddress?.address || '',
                        zipCode: user?.buyerAddress?.zipCode || '',
                    }));
                }

                setIsInitialized(true);
            } catch (error) {
                console.error(error);
            } finally {
                setIsPrefilling(false);
            }
        };

        prefillData();
    }, [
        isLogin,
        user,
        isExistingSeller,
        isInitialized,
        isForcedCompanyMode,
        isB2BUpgradeMode,
        companyModalAcknowledged,
        navigate,
    ]);

    useEffect(() => {
        const loadB2BReviewConfig = async () => {
            try {
                const response = await axios.get(`${API}/api/v1/settings/admin/b2b-config`, {
                    params: { _t: Date.now() },
                });
                setB2bReviewRequired(Boolean(response.data?.b2b?.sellerApprovalRequired));
            } catch (error) {
                setB2bReviewRequired(true);
            } finally {
                setB2bReviewConfigLoaded(true);
            }
        };

        loadB2BReviewConfig();
    }, []);

    useEffect(() => {
        const totalSteps = getSteps.length;
        if (currentStepNumber > totalSteps) {
            setCurrentStepNumber(totalSteps);
        }
        setCompletedSteps((prev) => prev.filter((step) => step <= totalSteps));
    }, [currentStepNumber, getSteps.length]);

    useEffect(() => {
        const imageMap = {
            1: image1,
            2: image3,
            3: image2,
            4: image4,
            5: image1,
            6: image3,
        };
        setImage(imageMap[currentStepNumber] || image1);
    }, [currentStepNumber]);

    const handleInputChange = (field, value) => {
        if (field === 'sellerType' && isForcedCompanyMode) {
            return;
        }

        const sanitizedValue = (() => {
            if ((field === 'telephone' || field === 'fax') && Array.isArray(value)) {
                return value.map((entry) => String(entry).replace(/[^\d]/g, '')).filter(Boolean);
            }

            if (field === 'productionCapacity' || field === 'inventoryCapacity') {
                return String(value).replace(/[^\d]/g, '');
            }

            return value;
        })();

        setFormData((prevData) => {
            if (field === 'sellerType') {
                if (value?.value === 'company' && !companyModalAcknowledged) {
                    setCompanyModalVisible(true);
                }
                return { ...prevData, sellerType: value };
            }

            if (field === 'country') {
                return {
                    ...prevData,
                    country: value,
                    state: value?.label === 'Canada' ? null : { name: '', tax: 5 },
                };
            }

            if (field === 'state' && prevData.country?.label === 'Canada') {
                return {
                    ...prevData,
                    state: {
                        name: sanitizedValue.label,
                        tax: sanitizedValue.value,
                    },
                };
            }

            return { ...prevData, [field]: sanitizedValue };
        });

        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const validateStep = (stepNumber) => {
        const errors = {};
        const currentStep = getSteps.find((s) => s.number === stepNumber);

        switch (currentStep?.text) {
            case 'Seller Type':
                if (!formData.sellerType) errors.sellerType = 'Please select a seller type';
                if (formData.sellerType?.value === 'company' && !companyModalAcknowledged) {
                    errors.sellerType = 'Please acknowledge company registration details';
                }
                break;

            case 'Basic Info':
                if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
                if (!formData.displayName.trim()) errors.displayName = 'Display name is required';
                if (!formData.sellerPicture && !formData.existingSellerPicture) {
                    errors.sellerPicture = 'Profile picture is required';
                }
                break;

            case 'Company Identity':
                if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
                if (!formData.natureOfOrg) errors.natureOfOrg = 'Nature of organization is required';
                if (formData.natureOfOrg === 'Other' && !formData.natureOfOrgOther.trim()) {
                    errors.natureOfOrgOther = 'Please specify your organization type';
                }
                if (!formData.numberOfEmployees) errors.numberOfEmployees = 'Number of employees is required';
                if (!formData.yearFounded) errors.yearFounded = 'Year founded is required';
                break;

            case 'Company Operations':
                if (!formData.productionCapacity.trim()) errors.productionCapacity = 'Production capacity is required';
                else if (!/^\d+$/.test(formData.productionCapacity.trim())) errors.productionCapacity = 'Production capacity must contain only numbers';
                if (!formData.inventoryCapacity.trim()) errors.inventoryCapacity = 'Inventory capacity is required';
                else if (!/^\d+$/.test(formData.inventoryCapacity.trim())) errors.inventoryCapacity = 'Inventory capacity must contain only numbers';
                if (!formData.productCategories.length) errors.productCategories = 'Select at least one product category';
                if (formData.telephone.some((entry) => !/^\d+$/.test(String(entry)))) {
                    errors.telephone = 'Telephone numbers must contain only digits';
                }
                if (formData.fax.some((entry) => !/^\d+$/.test(String(entry)))) {
                    errors.fax = 'Fax numbers must contain only digits';
                }
                break;

            case 'Additional Info':
                if (!formData.country) errors.country = 'Country is required';
                if (!formData.state || !formData.state?.name) errors.state = 'State is required';
                if (!formData.city.trim()) errors.city = 'City is required';
                if (!formData.address.trim()) errors.address = 'Address is required';
                if (!formData.services.length) errors.services = 'Please select at least one service';
                break;

            case 'Description':
                if (!formData.briefDescription.trim()) errors.briefDescription = 'Description is required';
                break;

            default:
                break;
        }

        return errors;
    };

    const handleNext = () => {
        const errors = validateStep(currentStepNumber);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setCompletedSteps((prev) => Array.from(new Set([...prev, currentStepNumber])));

        const currentIndex = getSteps.findIndex((s) => s.number === currentStepNumber);
        if (currentIndex < getSteps.length - 1) {
            setCurrentStepNumber(getSteps[currentIndex + 1].number);
        }
    };

    const handleBack = () => {
        const currentIndex = getSteps.findIndex((s) => s.number === currentStepNumber);
        if (currentIndex > 0) {
            setCurrentStepNumber(getSteps[currentIndex - 1].number);
        }
    };

    const handleStepNavigation = (targetStep) => {
        const currentIndex = getSteps.findIndex((s) => s.number === currentStepNumber);
        const targetIndex = getSteps.findIndex((s) => s.number === targetStep);

        if (targetIndex <= currentIndex || completedSteps.includes(targetStep)) {
            setCurrentStepNumber(targetStep);
        }
    };

    const handleMultiSelectChange = (field, selectedOptions) => {
        const selectedValues = selectedOptions.map((option) => option.label);
        handleInputChange(field, selectedValues);
    };

    const submitSellerProfile = async (token) => {
        const sellerFormData = new FormData();
        const companyEmployees = formData.noOfEmployees || String(employeeRangeToApprox(formData.numberOfEmployees));

        sellerFormData.append('sellerType', formData.sellerType.value);
        sellerFormData.append('country', JSON.stringify(formData.country));
        sellerFormData.append('state', JSON.stringify(formData.state));

        sellerFormData.append('fullName', formData.fullName);
        sellerFormData.append('displayName', formData.displayName);
        sellerFormData.append('services', JSON.stringify(formData.services));
        sellerFormData.append('city', formData.city);
        sellerFormData.append('address', formData.address);
        sellerFormData.append('zipCode', formData.zipCode || '');
        sellerFormData.append('description', formData.briefDescription);

        if (formData.sellerType.value === 'company') {
            sellerFormData.append('yearEstablished', formData.yearFounded || formData.yearEstablished || '');
            sellerFormData.append('noOfEmployees', companyEmployees);
        }

        if (formData.sellerPicture) {
            sellerFormData.append('profileImage', formData.sellerPicture);
        }

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`,
            },
        };

        if (isExistingSeller) {
            await axios.put(
                `${API}/api/v1/sellers/seller/${user.sellerId._id}`,
                sellerFormData,
                config
            );
            return;
        }

        await axios.post(
            `${API}/api/v1/sellers/seller/new`,
            sellerFormData,
            config
        );
    };

    const submitBusinessProfile = async (token) => {
        const payload = buildB2BPayload(formData);

        if (b2bProfile?.status === 'pending') {
            showToast('Your business profile is already pending review.', 'info');
            return;
        }

        try {
            await axios.post(`${API}/api/v1/b2b/seller/apply`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            const message = error?.response?.data?.message || '';
            if (message.toLowerCase().includes('already pending')) {
                showToast('Your business profile is already pending review.', 'info');
                return;
            }
            throw error;
        }
    };

    const finalizeSubmit = async () => {
        const errors = validateStep(currentStepNumber);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsLoading(true);

        try {
            const token = localStorage.getItem('token');
            await submitSellerProfile(token);

            if (formData.sellerType.value === 'company') {
                await submitBusinessProfile(token);
                showToast('Business seller profile submitted successfully.', 'success');
            } else {
                showToast('Seller profile completed successfully.', 'success');
            }

            await fetchUserData();

            if (formData.sellerType.value === 'company') {
                navigate('/sellerDashboard');
            } else {
                navigate('/sellerDashboard');
            }
        } catch (error) {
            console.error(error);
            showToast(error.response?.data?.error || error.response?.data?.message || 'Something went wrong', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateStep(currentStepNumber);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (isCompanySeller && !isExistingSeller && !b2bReviewConfigLoaded) {
            showToast('Please wait while business account settings are loading.', 'info');
            return;
        }

        const shouldShowReviewConfirmation =
            isCompanySeller &&
            !isExistingSeller &&
            b2bReviewRequired &&
            b2bReviewConfigLoaded;

        if (shouldShowReviewConfirmation) {
            setShowReviewConfirmationModal(true);
            return;
        }

        await finalizeSubmit();
    };

    const serviceOptions = [
        { value: 'delivery', label: 'Delivery' },
        { value: 'pickup', label: 'Pickup' },
        { value: 'export', label: 'Worldwide Shipping (Export)' },
    ];

    const b2bCategoryOptions = b2bCategories.map((category) => ({
        value: category.name,
        label: category.name,
    }));

    const renderCurrentStep = () => {
        const currentStep = getSteps.find((s) => s.number === currentStepNumber);

        switch (currentStep?.text) {
            case 'Seller Type':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">Seller Registration</h2>
                            <p className="topText">Choose your seller type</p>
                        </div>

                        {isForcedCompanyMode && (
                            <div className="becomeSellerHint">
                                <FaInfoCircle />
                                <p>
                                    To sell in B2B, your account is being upgraded as a business seller.
                                </p>
                            </div>
                        )}

                        <form className="signUpForm" onSubmit={(event) => { event.preventDefault(); handleNext(); }}>
                            <label htmlFor="sellerType" className="label">Seller Type</label>
                            <Dropdown
                                options={SELLER_TYPE_OPTIONS}
                                value={formData.sellerType}
                                onChange={(value) => handleInputChange('sellerType', value)}
                                placeholder="Select seller type"
                                disabled={isForcedCompanyMode}
                            />
                            {isForcedCompanyMode && (
                                <small className="becomeSellerLockText">Seller type is locked to Company in this upgrade flow.</small>
                            )}
                            {formErrors.sellerType && <div className="error-message">{formErrors.sellerType}</div>}

                            <button type="submit" className="primaryBtn">Next</button>
                        </form>
                    </div>
                );

            case 'Basic Info':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">Enter Your Details</h2>
                        </div>

                        <form className="signUpForm" onSubmit={(event) => { event.preventDefault(); handleNext(); }}>
                            <label htmlFor="sellerPicture" className="label">Seller Picture/Logo</label>
                            <div className="sellerPictureUpload">
                                <div className="uploadArea">
                                    {formData.sellerPicture || formData.existingSellerPicture ? (
                                        <div className="previewContainer">
                                            <img
                                                src={formData.sellerPicture ? URL.createObjectURL(formData.sellerPicture) : formData.existingSellerPicture}
                                                alt="Preview"
                                                className="previewImage"
                                            />
                                            <button
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    handleInputChange('sellerPicture', null);
                                                    handleInputChange('existingSellerPicture', '');
                                                    const input = document.getElementById('sellerPicture');
                                                    if (input) input.value = '';
                                                }}
                                                className="removeButton"
                                            >
                                                <FaPen />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="sellerUploadPlaceholder">
                                            <FaCamera className="uploadIcon" />
                                            <span>Seller Picture/Logo</span>
                                        </div>
                                    )}
                                    <input
                                        id="sellerPicture"
                                        type="file"
                                        accept="image/*"
                                        onChange={(event) => handleInputChange('sellerPicture', event.target.files[0])}
                                        className="fileInput"
                                    />
                                </div>
                                {formErrors.sellerPicture && <div className="error-message">{formErrors.sellerPicture}</div>}
                            </div>

                            <label htmlFor="fullName" className="label">Full Name</label>
                            <input
                                id="fullName"
                                type="text"
                                value={formData.fullName}
                                onChange={(event) => handleInputChange('fullName', event.target.value)}
                                className="inputField"
                                placeholder="Enter full name"
                            />
                            {formErrors.fullName && <div className="error-message">{formErrors.fullName}</div>}

                            <label htmlFor="displayName" className="label">Display Name</label>
                            <input
                                id="displayName"
                                type="text"
                                value={formData.displayName}
                                onChange={(event) => handleInputChange('displayName', event.target.value)}
                                className="inputField"
                                placeholder="Enter display name"
                            />
                            {formErrors.displayName && <div className="error-message">{formErrors.displayName}</div>}

                            <div className="formNavButtons">
                                <button type="button" className="secondaryBtn" onClick={handleBack}><FaArrowLeft /> Back</button>
                                <button type="submit" className="primaryBtn">Next</button>
                            </div>
                        </form>
                    </div>
                );

            case 'Company Identity':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">Company Identity</h2>
                            <p className="topText">Tell us about your company basics</p>
                        </div>

                        <form className="signUpForm" onSubmit={(event) => { event.preventDefault(); handleNext(); }}>
                            <label className="label">Company Name</label>
                            <input
                                type="text"
                                className="inputField"
                                value={formData.companyName}
                                onChange={(event) => handleInputChange('companyName', event.target.value)}
                                placeholder="Enter company name"
                            />
                            {formErrors.companyName && <div className="error-message">{formErrors.companyName}</div>}

                            <label className="label">Nature of Organization</label>
                            <Dropdown
                                options={ORG_OPTIONS}
                                value={ORG_OPTIONS.find((item) => item.value === formData.natureOfOrg)}
                                onChange={(value) => handleInputChange('natureOfOrg', value.value)}
                                placeholder="Select organization type"
                            />
                            {formErrors.natureOfOrg && <div className="error-message">{formErrors.natureOfOrg}</div>}

                            {formData.natureOfOrg === 'Other' && (
                                <>
                                    <label className="label">Specify Organization Type</label>
                                    <input
                                        type="text"
                                        className="inputField"
                                        value={formData.natureOfOrgOther}
                                        onChange={(event) => handleInputChange('natureOfOrgOther', event.target.value)}
                                        placeholder="Describe your organization"
                                    />
                                    {formErrors.natureOfOrgOther && <div className="error-message">{formErrors.natureOfOrgOther}</div>}
                                </>
                            )}

                            <label className="label">Number of Employees</label>
                            <Dropdown
                                options={EMPLOYEE_OPTIONS}
                                value={EMPLOYEE_OPTIONS.find((item) => item.value === formData.numberOfEmployees)}
                                onChange={(value) => handleInputChange('numberOfEmployees', value.value)}
                                placeholder="Select employee range"
                            />
                            {formErrors.numberOfEmployees && <div className="error-message">{formErrors.numberOfEmployees}</div>}

                            <label className="label">Year Founded</label>
                            <input
                                type="number"
                                className="inputField"
                                value={formData.yearFounded}
                                onChange={(event) => {
                                    handleInputChange('yearFounded', event.target.value);
                                    handleInputChange('yearEstablished', event.target.value);
                                }}
                                placeholder="Enter founding year"
                            />
                            {formErrors.yearFounded && <div className="error-message">{formErrors.yearFounded}</div>}

                            <label className="label">Website</label>
                            <input
                                type="text"
                                className="inputField"
                                value={formData.website}
                                onChange={(event) => handleInputChange('website', event.target.value)}
                                placeholder="https://example.com"
                            />

                            <div className="formNavButtons">
                                <button type="button" className="secondaryBtn" onClick={handleBack}><FaArrowLeft /> Back</button>
                                <button type="submit" className="primaryBtn">Next</button>
                            </div>
                        </form>
                    </div>
                );

            case 'Company Operations':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">Company Operations</h2>
                            <p className="topText">Provide business details required for B2B eligibility</p>
                        </div>

                        <form className="signUpForm" onSubmit={(event) => { event.preventDefault(); handleNext(); }}>
                            <TagInput
                                label="Telephone Numbers"
                                values={formData.telephone}
                                onChange={(values) => handleInputChange('telephone', values)}
                                placeholder="Type a number and click Add"
                                error={formErrors.telephone}
                                numericOnly
                            />

                            <TagInput
                                label="Fax Numbers"
                                values={formData.fax}
                                onChange={(values) => handleInputChange('fax', values)}
                                placeholder="Type a fax number and click Add"
                                error={formErrors.fax}
                                numericOnly
                            />

                            <label className="label">Production Capacity (per month)</label>
                            <input
                                type="number"
                                className="inputField"
                                value={formData.productionCapacity}
                                onChange={(event) => handleInputChange('productionCapacity', event.target.value)}
                                inputMode="numeric"
                                min="0"
                                step="1"
                                placeholder="e.g. 10000"
                            />
                            {formErrors.productionCapacity && <div className="error-message">{formErrors.productionCapacity}</div>}

                            <label className="label">Inventory Capacity</label>
                            <input
                                type="number"
                                className="inputField"
                                value={formData.inventoryCapacity}
                                onChange={(event) => handleInputChange('inventoryCapacity', event.target.value)}
                                inputMode="numeric"
                                min="0"
                                step="1"
                                placeholder="e.g. 6000"
                            />
                            {formErrors.inventoryCapacity && <div className="error-message">{formErrors.inventoryCapacity}</div>}

                            <label className="label">Product Categories</label>
                            <MultiSelect
                                options={b2bCategoryOptions}
                                value={formData.productCategories}
                                onChange={(selectedOptions) => handleMultiSelectChange('productCategories', selectedOptions)}
                                placeholder="Select business product categories"
                            />
                            {formErrors.productCategories && <div className="error-message">{formErrors.productCategories}</div>}

                            {/* Export Markets with Worldwide option */}
                            <div style={{ marginBottom: '16px' }}>
                                <label className="label" style={{ display: 'block', marginBottom: '10px' }}>Export Markets</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', cursor: 'pointer', userSelect: 'none' }}>
                                    <input
                                        type="checkbox"
                                        checked={formData.exportMarketsWorldwide}
                                        onChange={(e) => {
                                            handleInputChange('exportMarketsWorldwide', e.target.checked);
                                            if (e.target.checked) handleInputChange('exportMarkets', []);
                                        }}
                                        style={{ width: '18px', height: '18px', accentColor: '#4CAF50', cursor: 'pointer' }}
                                    />
                                    <span style={{ fontWeight: '600', color: '#333' }}>🌍 Worldwide (ship to all countries)</span>
                                </label>
                                {!formData.exportMarketsWorldwide && (
                                    <MultiSelect
                                        options={countries.map(c => ({ value: c.code, label: c.name }))}
                                        value={formData.exportMarkets}
                                        onChange={(selectedOptions) => handleInputChange('exportMarkets', selectedOptions.map(o => o.label))}
                                        placeholder="Search and select target countries…"
                                    />
                                )}
                                {formData.exportMarketsWorldwide && (
                                    <p style={{ color: '#4CAF50', fontSize: '14px', margin: '8px 0 0 0', fontWeight: '500' }}>✓ Selling to all countries worldwide</p>
                                )}
                            </div>

                            <TagInput
                                label="Certifications"
                                values={formData.certifications}
                                onChange={(values) => handleInputChange('certifications', values)}
                                placeholder="Type a certification and click Add"
                            />

                            <div className="formNavButtons">
                                <button type="button" className="secondaryBtn" onClick={handleBack}><FaArrowLeft /> Back</button>
                                <button type="submit" className="primaryBtn">Next</button>
                            </div>
                        </form>
                    </div>
                );

            case 'Additional Info':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">Additional Information</h2>
                        </div>

                        <form className="signUpForm" onSubmit={(event) => { event.preventDefault(); handleNext(); }}>
                            <label htmlFor="services" className="label">Services</label>
                            <MultiSelect
                                options={serviceOptions}
                                value={formData.services}
                                onChange={(selectedOptions) => handleMultiSelectChange('services', selectedOptions)}
                                placeholder="Select services"
                            />
                            {formErrors.services && <div className="error-message">{formErrors.services}</div>}

                            <p className="label pickupAddressHeading">Pickup / Business Address</p>
                            <p className="pickupAddressHint">Prefilled from your delivery address. Change it if buyers collect from somewhere else; you can edit it any time in Settings.</p>

                            <div className="formRow2Col">
                                <div className="formCol">
                                    <label htmlFor="country" className="label">Country</label>
                                    <Dropdown
                                        id="country"
                                        options={countries.map((country) => ({ value: country.code, label: country.name }))}
                                        value={formData.country}
                                        onChange={(value) => handleInputChange('country', value)}
                                        placeholder="Select Country"
                                    />
                                    {formErrors.country && <div className="error-message">{formErrors.country}</div>}
                                </div>

                                <div className="formCol">
                                    <label htmlFor="state" className="label">State/Province</label>
                                    {formData.country?.label === 'Canada' ? (
                                        <Dropdown
                                            id="state"
                                            options={states.map((state) => ({ value: state.tax, label: state.name }))}
                                            value={formData.state ? { value: formData.state.tax, label: formData.state.name } : null}
                                            onChange={(value) => handleInputChange('state', value)}
                                            placeholder="Select State"
                                        />
                                    ) : (
                                        <input
                                            id="state"
                                            type="text"
                                            value={formData.state?.name || ''}
                                            onChange={(event) => handleInputChange('state', { name: event.target.value, tax: 5 })}
                                            className="inputField"
                                            placeholder="State/Province"
                                        />
                                    )}
                                    {formErrors.state && <div className="error-message">{formErrors.state}</div>}
                                </div>
                            </div>

                            <div className="formRow2Col">
                                <div className="formCol">
                                    <label htmlFor="city" className="label">City</label>
                                    <input
                                        id="city"
                                        type="text"
                                        value={formData.city}
                                        onChange={(event) => handleInputChange('city', event.target.value)}
                                        className="inputField"
                                        placeholder="City"
                                    />
                                    {formErrors.city && <div className="error-message">{formErrors.city}</div>}
                                </div>

                                <div className="formCol">
                                    <label htmlFor="zipCode" className="label">Zip/Postal Code</label>
                                    <input
                                        id="zipCode"
                                        type="text"
                                        value={formData.zipCode}
                                        onChange={(event) => {
                                            handleInputChange('zipCode', event.target.value);
                                            handleInputChange('postalCode', event.target.value);
                                        }}
                                        className="inputField"
                                        placeholder="Zip/Postal Code"
                                    />
                                </div>
                            </div>

                            <label htmlFor="address" className="label">Address</label>
                            <input
                                id="address"
                                type="text"
                                value={formData.address}
                                onChange={(event) => handleInputChange('address', event.target.value)}
                                className="inputField"
                                placeholder="Street address"
                            />
                            {formErrors.address && <div className="error-message">{formErrors.address}</div>}

                            <div className="formNavButtons">
                                <button type="button" className="secondaryBtn" onClick={handleBack}><FaArrowLeft /> Back</button>
                                <button type="submit" className="primaryBtn">Next</button>
                            </div>
                        </form>
                    </div>
                );

            case 'Description':
                return (
                    <div className="signUpContent">
                        <div className="headerText">
                            <h2 className="welcomeTitle">About Your Business</h2>
                        </div>

                        {b2bProfile?.status === 'declined' && (
                            <div className="becomeSellerHint" style={{ borderColor: '#f59e0b', background: '#fff7ed', color: '#b45309' }}>
                                <FaInfoCircle />
                                <p>
                                    Your previous business application was declined. Update details below and resubmit.
                                    {b2bProfile.adminNote ? ` Reason: ${b2bProfile.adminNote}` : ''}
                                </p>
                            </div>
                        )}

                        <form className="signUpForm" onSubmit={handleSubmit}>
                            <label htmlFor="briefDescription" className="label">Business description</label>
                            <textarea
                                id="briefDescription"
                                value={formData.briefDescription}
                                onChange={(event) => handleInputChange('briefDescription', event.target.value)}
                                className="inputField"
                                placeholder="Tell us about your business"
                                maxLength="600"
                                rows="6"
                            />
                            {formErrors.briefDescription && <div className="error-message">{formErrors.briefDescription}</div>}

                            <p className="wordCount">* Max 600 words ({formData.briefDescription.length}/600)</p>

                            <div className="formNavButtons">
                                <button type="button" className="secondaryBtn" onClick={handleBack}><FaArrowLeft /> Back</button>
                                <button type="submit" className="primaryBtn">
                                    {isCompanySeller
                                        ? (b2bReviewRequired ? 'Submit Business Seller Profile' : 'Become a Business Seller')
                                        : 'Become a Seller'}
                                </button>
                            </div>
                        </form>
                    </div>
                );

            default:
                return null;
        }
    };

    if (isPrefilling) {
        return <Loader type="simpleMax" />;
    }

    return (
        <div className="signUpContainer becomeSellerDiv">
            {(isLoading || isPrefilling) && <Loader type="checkmate" />}

            {companyModalVisible && (
                <div className="becomeSellerCompanyModalOverlay">
                    <div className="becomeSellerCompanyModal">
                        <h3>Register As A Business Seller</h3>
                        <p>
                            {b2bReviewRequired
                                ? 'You are about to continue with a business account. Please provide complete and accurate business details so your profile can be reviewed and approved for B2B trading.'
                                : 'You are about to continue with a business account. Please provide complete and accurate business details to activate your business seller profile for B2B trading.'}
                        </p>
                        <ul className="becomeSellerCompanyModalList">
                            <li>Your will become a Business Seller.</li>
                            <li>Business details must match your actual registered company.</li>
                            {b2bReviewRequired && (
                                <li>Approval may be required to be eligible to sell on B2B Marketplace.</li>
                            )}
                        </ul>
                        <button
                            type="button"
                            className="primaryBtn"
                            onClick={() => {
                                setCompanyModalAcknowledged(true);
                                setCompanyModalVisible(false);
                            }}
                        >
                            I Understand
                        </button>
                    </div>
                </div>
            )}

            {showReviewConfirmationModal && (
                <div className="becomeSellerCompanyModalOverlay">
                    <div className="becomeSellerCompanyModal becomeSellerReviewConfirmModal">
                        <h3>Submit Business Request For Review?</h3>
                        <p>
                            Your seller account will be created immediately after submission. Your B2B selling privileges will become active once your business profile is approved by the admin team.
                        </p>
                        <div className="becomeSellerReviewInfoRow">
                            <FaInfoCircle />
                            <span>You can still access seller dashboard features while your B2B request is under review.</span>
                        </div>
                        <div className="formNavButtons" style={{ justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="secondaryBtn"
                                onClick={() => setShowReviewConfirmationModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="primaryBtn"
                                onClick={async () => {
                                    setShowReviewConfirmationModal(false);
                                    await finalizeSubmit();
                                }}
                            >
                                Submit Request
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="steps-navigation becomeSellerNavigation">
                {getSteps.map((stepInfo, index) => (
                    <React.Fragment key={stepInfo.number}>
                        <StepTab
                            label={stepInfo.number}
                            text={stepInfo.text}
                            isActive={currentStepNumber === stepInfo.number}
                            isCompleted={completedSteps.includes(stepInfo.number)}
                            isClickable={completedSteps.includes(stepInfo.number) || stepInfo.number === currentStepNumber}
                            onClick={() => handleStepNavigation(stepInfo.number)}
                        />
                        {index < getSteps.length - 1 && <div className="step-connector" />}
                    </React.Fragment>
                ))}
            </div>

            <div className="signUpSection becomeSellerSection">
                <div className="leftSection becomeSellerLeftSection">
                    {renderCurrentStep()}
                </div>

                {currentStepNumber !== 2 && currentStepNumber !== getSteps.length && (
                    <div className="rightSection becomeSellerRightSection">
                        <img key={currentStepNumber} src={image} alt="Seller" className="centerImage" />
                    </div>
                )}

                {(currentStepNumber === 2 || currentStepNumber === getSteps.length) && (
                    <div
                        className="rightSection becomeSellerRightSection"
                        style={{
                            backgroundImage: `url(${currentStepNumber === getSteps.length ? secondBackground : firstBackground})`,
                            backgroundColor: 'transparent',
                        }}
                    >
                        <img key={currentStepNumber} src={image} alt="Seller" className="centerImage" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SellerSignUpForm;
